import { DUE_BUCKET } from '../constants/filterOptions.js';

/** Epoch ms of an event's due date, or null when it has none / is unparseable. */
export function dueTimestamp(event) {
  if (!event?.dueAt) return null;
  const at = new Date(event.dueAt).getTime();
  return Number.isNaN(at) ? null : at;
}

/**
 * How many in-scope events fall in each due bucket, keyed by {@link DUE_BUCKET}
 * value. Feeds `buildDueOptions` so the Due filter only offers buckets that
 * actually have rows.
 *
 * `overdue` is counted off the cross-cutting `overdue` flag rather than off
 * `dueBucket`, because that is how the filter matches it: an overdue event keeps
 * its real bucket (e.g. `due_today`) and additionally sets `overdue: true`, so
 * it is counted in both.
 *
 * @param {Array<{dueBucket?: string, overdue?: boolean}>} events
 * @returns {Record<string, number>}
 */
export function countDueBuckets(events = []) {
  return events.reduce((acc, event) => {
    const bucket = event.dueBucket ?? DUE_BUCKET.NO_DATE;
    acc[bucket] = (acc[bucket] ?? 0) + 1;
    if (event.overdue) acc[DUE_BUCKET.OVERDUE] = (acc[DUE_BUCKET.OVERDUE] ?? 0) + 1;
    return acc;
  }, {});
}

/**
 * Due-date comparator, ordering on the real ISO timestamp — never on `dueLabel`,
 * which is localized display text ("Expired", "Due today"), so comparing it
 * sorted four English literals alphabetically and left every dateless row
 * wherever the fan-out happened to put it.
 *
 * Rows with no date always sort last, in both directions. Direction follows the
 * severity convention: the default `desc` is most-urgent-first (soonest date),
 * `asc` is furthest-out-first.
 */
export function compareByDueDate(a, b, direction = 'desc') {
  const aAt = dueTimestamp(a);
  const bAt = dueTimestamp(b);
  if (aAt === null || bAt === null) {
    if (aAt === bAt) return 0;
    return aAt === null ? 1 : -1;
  }
  return (aAt - bAt) * (direction === 'asc' ? -1 : 1);
}
